<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ExternalLink, Check, Loader2, AlertCircle, RefreshCw } from 'lucide-vue-next'
import { API, HOST } from '@/utils/http'
import type { BridgeLink } from '@/types/bridge'

const store = useStore()
const router = useRouter()
const currentUser = computed(() => store.getters['user/getMember'])

// State
const loading = ref(true)
const error = ref<string | null>(null)
const pendingLink = ref<BridgeLink | null>(null)
const refreshing = ref(false)

// Check if ToS is completed (approved)
const tosCompleted = computed(() => {
  return pendingLink.value?.tos_status === 'approved'
})

// Check if KYC is completed (approved)
const kycCompleted = computed(() => {
  return pendingLink.value?.kyc_status === 'approved'
})

// All steps completed
const allCompleted = computed(() => {
  return tosCompleted.value && kycCompleted.value
})

// Fetch pending KYC link
const fetchPendingLink = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/kyc-links/pending`
    })

    if (response.data.has_pending && response.data.link) {
      pendingLink.value = response.data.link
    } else {
      pendingLink.value = null
    }
  } catch (err: any) {
    console.error('Error fetching pending link:', err)
    error.value = err.response?.data?.detail || 'Failed to load verification status'
  } finally {
    loading.value = false
  }
}

// Refresh status
const refreshStatus = async () => {
  refreshing.value = true
  await fetchPendingLink()
  refreshing.value = false
}

const openTosLink = () => {
  if (pendingLink.value?.tos_url) {
    window.open(pendingLink.value.tos_url, '_blank', 'noopener,noreferrer')
  }
}

const openKycLink = () => {
  if (pendingLink.value?.kyc_url) {
    window.open(pendingLink.value.kyc_url, '_blank', 'noopener,noreferrer')
  }
}

const goToDashboard = () => {
  router.push({ name: 'Dashboard' })
}

onMounted(() => {
  if (!currentUser.value?.member_id) {
    router.push({ name: 'Auth' })
    return
  }
  fetchPendingLink()
})
</script>

<template>
  <div class="pb-2">
    <div class="p-3">
      <h1 class="text-xl font-semibold mb-1">Payment Verification</h1>
      <p class="text-sm text-muted-foreground mb-6">
        Complete these steps to enable payments on your account.
      </p>

      <!-- Loading state -->
      <div v-if="loading" class="py-8 flex flex-col items-center justify-center text-muted-foreground">
        <Loader2 class="h-6 w-6 animate-spin mb-3" />
        <p class="text-sm">Loading verification status...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="py-8">
        <div class="flex flex-col items-center justify-center text-center">
          <AlertCircle class="h-10 w-10 text-red-500 mb-3" />
          <p class="text-red-600 text-sm mb-4">{{ error }}</p>
          <Button variant="outline" size="sm" @click="fetchPendingLink">
            Try Again
          </Button>
        </div>
      </div>

      <!-- No pending verification -->
      <div v-else-if="!pendingLink" class="py-8">
        <div class="flex flex-col items-center justify-center text-center">
          <Check class="h-10 w-10 text-green-500 mb-3" />
          <p class="font-medium mb-1">No verification required</p>
          <p class="text-sm text-muted-foreground mb-4">
            You don't have any pending verification steps.
          </p>
          <Button size="sm" @click="goToDashboard">
            Go to Dashboard
          </Button>
        </div>
      </div>

      <!-- Verification steps -->
      <div v-else class="space-y-3">
        <!-- All completed message -->
        <div v-if="allCompleted" class="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <div class="flex items-center gap-2">
            <Check class="h-5 w-5 text-green-600" />
            <div>
              <p class="font-medium text-green-800 text-sm">Verification complete!</p>
              <p class="text-xs text-green-600">Your account is ready for payments.</p>
            </div>
          </div>
        </div>

        <!-- Step 1: ToS -->
        <div
          class="flex items-center gap-3 p-3 rounded-lg border"
          :class="tosCompleted ? 'bg-green-50 border-green-200' : 'bg-white'"
        >
          <div
            class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
            :class="tosCompleted ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'"
          >
            <Check v-if="tosCompleted" class="h-4 w-4" />
            <span v-else>1</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm" :class="tosCompleted ? 'text-green-700' : ''">
              Accept Terms of Service
            </p>
            <p class="text-xs text-muted-foreground">
              Review and accept the payment terms
            </p>
          </div>
          <div class="flex-shrink-0">
            <Badge v-if="tosCompleted" class="bg-green-100 text-green-700 border-green-200">
              Done
            </Badge>
            <Button
              v-else
              size="sm"
              @click="openTosLink"
              :disabled="!pendingLink?.tos_url"
            >
              Open
              <ExternalLink class="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>

        <!-- Step 2: KYC -->
        <div
          class="flex items-center gap-3 p-3 rounded-lg border"
          :class="[
            kycCompleted ? 'bg-green-50 border-green-200' : tosCompleted ? 'bg-white' : 'bg-gray-50 border-gray-200'
          ]"
        >
          <div
            class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
            :class="kycCompleted ? 'bg-green-100 text-green-600' : tosCompleted ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'"
          >
            <Check v-if="kycCompleted" class="h-4 w-4" />
            <span v-else>2</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm" :class="kycCompleted ? 'text-green-700' : !tosCompleted ? 'text-gray-400' : ''">
              Identity Verification (KYC)
            </p>
            <p class="text-xs" :class="!tosCompleted ? 'text-gray-400' : 'text-muted-foreground'">
              {{ !tosCompleted ? 'Complete step 1 first' : 'Verify your identity to enable payments' }}
            </p>
          </div>
          <div class="flex-shrink-0">
            <Badge v-if="kycCompleted" class="bg-green-100 text-green-700 border-green-200">
              Done
            </Badge>
            <Button
              v-else
              size="sm"
              @click="openKycLink"
              :disabled="!tosCompleted || !pendingLink?.kyc_url"
            >
              Verify
              <ExternalLink class="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>

        <!-- Refresh button -->
        <div class="pt-3">
          <Button variant="outline" size="sm" @click="refreshStatus" :disabled="refreshing">
            <RefreshCw class="h-3 w-3 mr-1" :class="{ 'animate-spin': refreshing }" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
